/**
 * studioApi.ts
 *
 * Thin client for the Crosure Studio Tenant Client API. Framework-independent
 * (plain fetch, no Next.js/React deps) so any tenant app can use it.
 *
 * Configuration is injected via createStudioApiClient({ apiBase, apiKey })
 * rather than read from environment variables directly, since each tenant
 * app has its own STUDIO_API_URL / STUDIO_API_KEY.
 *
 *   apiBase — base URL of the Studio backend, e.g. https://studio.crosure.com
 *             Trailing slash is stripped automatically.
 *   apiKey  — raw API key issued from Studio (TenantAPIKey).
 *             Sent as: Authorization: Bearer <apiKey>
 *             Scopes required: forms:read (schema fetch), submissions:write (submit)
 *
 * Endpoints used:
 *   GET  /api/client/v1/forms/{form_code}/schema/
 *   POST /api/client/v1/forms/{form_code}/submit/
 *   POST /api/client/v1/forms/{form_code}/quote-preview/
 *   GET  /api/client/v1/site-navigation/     (scope: content:read)
 *   GET  /api/client/v1/theme/                (scope: content:read)
 */
import type {
	StudioPageSectionConfig,
	StudioPageSectionType,
	TenantPageBuilderConfig,
} from './pageBuilder'

export interface StudioSectionMeta {
	/** Live API uses section_code; static export uses code */
	section_code?: string
	code?: string
	title: string
	description?: string
	display_order: number
	/** Live API provides the field list here; static export derives it from ui:section */
	fields?: string[]
}

export interface StudioFormSchemaResponse {
	schema: {
		$schema: string
		title: string
		description: string
		type: 'object'
		properties: Record<string, StudioFieldSchema>
		required: string[]
		/** Present in static export only */
		'x-crosure'?: {
			form_code: string
			product_code: string | null
			tenant_country: string | null
			sections: Array<StudioSectionMeta>
		}
	}
	uiSchema: Record<string, StudioUiEntry>
	/** Present in live API response only */
	meta?: {
		form_code: string
		product_code: string | null
		tenant_code: string | null
		status: string
		sections: Array<StudioSectionMeta>
		tenant_format?: {
			date_format?: string
			time_format?: string
			number_format?: string
			currency?: string
			timezone?: string
		}
		branding?: {
			logo_url?: string
			primary_color?: string
			tagline?: string
		}
		exported_at: string
		crosure_studio_version: string
	}
}

/**
 * Contract identifiers Studio's form-schema endpoint can be asked to return
 * via the `contract` query param. Currently only 'wizard-v1' — Studio
 * pre-adapts the flat schema into the same step-array shape
 * adaptStudioSchema() would otherwise produce from StudioFormSchemaResponse,
 * so the caller can skip that adaptation step entirely.
 */
export type StudioFormContract = 'wizard-v1'

/**
 * Returned instead of StudioFormSchemaResponse when the caller requested
 * `contract=wizard-v1` and Studio supports it for this form. `steps` are
 * already section-adapted (the same shape adaptStudioSchema() produces) —
 * callers must NOT run this through adaptStudioSchema again.
 */
export interface StudioWizardFormSchemaResponse {
	contract_version: 'wizard-v1'
	steps: Array<Record<string, unknown>>
	meta?: StudioFormSchemaResponse['meta']
}

export interface StudioFieldSchema {
	title: string
	type: string
	format?: string
	description?: string
	enum?: string[]
	enumNames?: string[]
	minimum?: number
	maximum?: number
	minLength?: number
	maxLength?: number
	pattern?: string
	default?: unknown
	items?: Record<string, unknown>
	maxItems?: number
	'x-visibility'?: unknown
	'x-crosure-repeat-presentation'?: {
		background_color?: string
		border_radius?: number
	}
	/** Studio's numeric controls. `step` is measured from `min`, not from zero,
	 *  so it is not the same rule as the `multipleOf` emitted beside it. */
	'x-crosure-numeric'?: {
		min?: number
		max?: number
		step?: number
		controls?: { input?: boolean; slider?: boolean; stepper?: boolean }
		show_value?: boolean
		show_min_max?: boolean
		format?: {
			style?: 'number' | 'currency' | 'percent' | 'unit'
			prefix?: string
			suffix?: string
			currency?: string
		}
	}
	/** Studio's date controls, already resolved against the tenant's clock —
	 *  `min`/`max`/`today` are ISO dates, or null where unbounded. */
	'x-crosure-date'?: {
		allowed?: { past?: boolean; today?: boolean; future?: boolean }
		window?: { days_before?: number | null; days_after?: number | null }
		quick_actions?: { today?: boolean; tomorrow?: boolean }
		today?: string
		min?: string | null
		max?: string | null
	}
	/** Studio's Checkbox field — the consent sentence, as sanitized HTML. */
	'x-crosure-checkbox'?: {
		content_html?: string
		content_i18n?: Record<string, string>
		show_label?: boolean
	}
	/** Consumer-injected metadata for the address-step special-case (not part
	 *  of Studio's schema — set by adaptStudioSchema for AddressField). */
	button?: string
	select?: string
	error?: string
}

export interface StudioUiEntry {
	'ui:order': number
	'ui:widget'?: string
	'ui:help'?: string
	'ui:section'?: string
	'ui:options'?: Record<string, unknown>
	'ui:ArrayFieldTemplate'?: string
}

export interface StudioSubmitResponse {
	id: string
	form_code: string
	status: string
	created: string
	[key: string]: unknown
}

/** Error that preserves Studio's HTTP status and full response body so the
 *  caller can forward them verbatim (e.g. 400 validation with field errors)
 *  instead of collapsing everything into an opaque 502. */
export class StudioApiError extends Error {
	status: number
	body: any
	constructor(status: number, body: any) {
		super(
			body?.detail ||
				body?.message ||
				`Studio request failed with status ${status}`
		)
		this.name = 'StudioApiError'
		this.status = status
		this.body = body
	}
}

export class StudioLoginError extends Error {
	status: number
	constructor(status: number, message: string) {
		super(message)
		this.status = status
	}
}

export interface LiveVirtualAgent {
	agent_code: string
	chat_url: string
	name: string
}

export interface StudioSiteNavigationResponse {
	/** Browser/OG title. Studio resolves this to the tenant's own name when
	 *  no explicit override is set, so it is never blank on a live response. */
	site_name: string
	/** Meta description, from Studio's `branding.tagline`. Blank means "not
	 *  configured" — the caller keeps its own bundle tagline. */
	tagline: string
	logo_url: string
	nav_primary: Array<{ label: string; href: string }>
	nav_secondary: Array<{ label: string; href: string }>
	social_links: Array<{ label: string; href: string }>
	footer_headline: string
	footer_legal_text: string
	registered_address: string
	/** Present on the endpoint since the language-switcher work; this
	 *  interface had simply drifted behind it. */
	language_switcher_visible: boolean
	language_switcher_position: 'left' | 'center' | 'right'
}

export interface StudioThemeResponse {
	primary_color: string
	secondary_color: string
	accent_color: string
	success_color: string
	warning_color: string
	destructive_color: string
	font_heading: string
	font_body: string
	radius_scale: string
}

export interface StudioPageSection<
	T extends StudioPageSectionType = StudioPageSectionType,
> {
	id: string
	section_type: T
	display_order: number
	config: StudioPageSectionConfig<T>
}

export interface StudioPageSchema {
	page_code: string
	slug: string
	name: string
	title: string
	description: string
	meta_description: string
	layout: string
	status: string
	locale_used?: string
	default_locale?: string
	supported_locales?: string[]
	direction?: 'ltr' | 'rtl'
	page_builder?: TenantPageBuilderConfig | null
	product: Record<string, unknown> | null
	sections: StudioPageSection[]
}

export interface StudioQuotePreviewResponse {
	annualPremium: string | null
	paymentFrequency: string
	currency: string
	/** Tenant number convention, e.g. "1,234.56" | "1.234,56" | "1 234.56". */
	numberFormat?: string
	basePremium: string | null
	appliedMin: boolean
	appliedMax: boolean
	breakdown: Array<{
		coverage_code: string
		name: string
		base: string | null
		final: string | null
		factors: Array<{
			factor_code: string
			name: string
			factor_type: string
			modifier: string | null
		}>
	}>
	benefits?: StudioQuoteBenefit[]
	sections?: StudioQuoteSection[]
	paymentPlan?: StudioQuotePaymentPlan
	/** Every heading the page prints that is not a value from the quote —
	 *  authored per tenant in Studio, resolved for the requested locale. A
	 *  renderer prints what is here and nothing of its own; a key that is
	 *  absent means that heading, and any row it belongs to, is not shown. */
	labels?: Record<string, string>
	/** Precomputed matrix for a Studio-authored price_review page. Absent
	 *  unless the form authors price_review blocks, so every existing tenant
	 *  sees the same response it always did. */
	options?: StudioPriceReviewOptions
	underwriting: {
		outcome: string
		reason_code: string
		reason_message: string
		triggered_rule: string | null
	}
}

/** One block of a Studio-authored price_review page. `type` selects the
 *  renderer; everything else is authored content, already resolved to the
 *  requested locale server-side — the consumer app never picks a language.
 *  An unknown `type` must degrade to title + description, never throw. */
export interface StudioPriceReviewBlock {
	code: string
	type:
		| 'frequency'
		| 'excess'
		| 'breakdown'
		| 'addons'
		| 'sections'
		| 'benefits'
		| 'terms'
		| 'payment_plan'
		| 'premium_basis'
		| 'values'
		| 'monthly_note'
		| 'note'
		| 'documents'
		| string
	title?: string
	description?: string
	/** Form field code this block's selection is stored under. */
	answer_field?: string
	/** excess: the rate table of (excess, discount%) rows. */
	table_code?: string
	/** excess: value selected before the customer touches the stepper. */
	default_value?: string | number
	/** excess: a rating factor this table supersedes — switched off while the
	 *  basic is rated so the discount is not applied twice. */
	factor_code?: string
	control?: 'stepper' | 'radio'
	coverage_codes?: string[]
	show_sum_insured?: boolean
	footer?: string
	add_label?: string
	decline_label?: string
	unavailable_label?: string
	visibility?: Record<string, unknown>
	/** breakdown: show the grey footer (tax, validity, quote reference). Its
	 *  wording is fixed; every value in it comes from the quote. */
	show_footer?: boolean
	/** breakdown: legacy authoring — rows bound to a rating factor. Rows now
	 *  come from the priced payload, so new blocks author only copy. */
	rows?: Array<{ factor_code: string; title?: string }>
	/** addons: answer-driven add-ons. Choosing one writes `on_value` to
	 *  `answer_field`, so it re-rates through the ordinary submission path.
	 *  `visibility` lets one element carry every variant of a cover and show
	 *  only those the customer's answers make relevant. */
	items?: Array<{
		answer_field: string
		on_value?: string
		off_value?: string
		title?: string
		description?: string
		visibility?: Record<string, unknown>
	}>
	/** frequency: display controls. Prices always come from the API. */
	show_price?: boolean
	price_source?: 'auto' | 'instalment' | 'total'
	show_instalment_detail?: boolean
	/** documents: a StaticContent config (title, description, links, items,
	 *  footnote…) rendered by the same help-box widget the rest of the form
	 *  uses. */
	content?: Record<string, unknown>
	/** breakdown: close the card with the quote's premium. */
	show_total?: boolean
	/** sections / breakdown: print the detail rows. Off shows totals only —
	 *  the per-coverage breakdown toggle the section has always carried. */
	show_rows?: boolean
	/** breakdown: head the card with the cover heading and the price column's
	 *  own header ("Price/yr"). */
	show_card_header?: boolean
	/** Row headings a block prints itself, authored so a journey in any
	 *  language gets its own wording rather than this app's English. Each falls
	 *  back to the wording the page has always used.
	 *  breakdown/payment_plan: `total_label`. terms: the other three.
	 *  payment_plan: `final_instalment_label`, `other_options_label`. */
	total_label?: string
	excess_label?: string
	waiting_period_label?: string
	exclusions_label?: string
	final_instalment_label?: string
	other_options_label?: string
}

export interface StudioPriceReviewExcessRow {
	/** null when the form authors no usable excess block — a single row
	 *  priced from the payload as-is. */
	value: string | null
	/** Total per frequency code, e.g. {"annual": "233.69 GBP"}. */
	totals: Record<string, string>
	/** Premium with every add-on declined. Decimal string. */
	base_total: string
	/** Mandatory cover rows: coverage_code → premium. */
	coverages: Record<string, string>
	/** Add-on prices at this band: coverage_code → premium. */
	addons: Record<string, string>
}

export interface StudioPriceReviewOptions {
	frequencies: Array<{
		code: string | null
		label: string | null
		frequency: string | null
		instalments: number | null
		instalment_amount: string | null
		total: string | null
	}>
	excess: {
		/** Form field code the selected excess is submitted under. */
		field: string | null
		selected: string | null
		/** The rate table: [excess, discount%] rows, ascending. The stepper's
		 *  options are these rows, so it cannot offer an unpriceable value. */
		table?: Array<[string, string]>
		/** Compulsory minimum — row 0. No discount at or below it. */
		minimum?: string | null
		/** Signed credit for the current selection, e.g. "-11.68". */
		premium?: string | null
		values: StudioPriceReviewExcessRow[]
	}
	addons: Array<{
		coverage_code: string
		name: string
		sum_insured: string
		/** Present for answer-driven add-ons. */
		answer_field?: string
		on_value?: string
		off_value?: string
		description?: string
		visibility?: Record<string, unknown>
	}>
	/** The cost breakdown: named, priced rows, ready to render. Includes an
	 *  `excess` row once the excess earns a credit — informational, since the
	 *  other amounts are already net of it, so never sum these. */
	rows?: Array<{
		code: string
		name: string
		value: string
		/** Set when the row's code matches a coverage carrying a sum insured. */
		sum_insured?: string | null
		/** Authored text shown instead of a sum insured, when the tenant wrote
		 *  one for that coverage ("Up to GBP 1,500,000"). */
		display_value?: string | null
	}>
	note: {
		/** null when no IPT factor fired — say nothing rather than "0%". */
		ipt_percent?: string | null
		validity_days?: number
		/** Null on a preview; a reference exists only once a quote is saved. */
		quote_reference?: string | null
	}
	/** Authoring problems (missing factor, no discrete bands). Surfaced in
	 *  Studio; never rendered to a customer. */
	diagnostics?: string[]
}

export interface StudioQuoteBenefit {
	coverage_code: string
	name: string
	coverage_type: string
	coverage_type_label: string
	sum_insured: string | null
	display_value: string | null
	deductible_type: string
	deductible_type_label: string
	deductible_value: string | null
	exclusions: string[]
	waiting_period_days: number
	notes: string
}

export interface StudioQuoteSectionRow {
	code: string
	name: string
	value: string
}

export interface StudioQuoteSectionGroup {
	code: string
	title: string
	rows: StudioQuoteSectionRow[]
	subtotal: { name: string; value: string } | null
}

export interface StudioQuoteSection {
	code: string
	title: string
	value_header: string
	style: string
	groups: StudioQuoteSectionGroup[]
	total: { name: string; value: string } | null
}

export interface StudioQuotePaymentPlanOption {
	option: string
	label: string
	frequency: string
	recurring: boolean
	instalments: number
	instalment_amount: string
	/** Carries the rounding remainder, so it can differ from the others. */
	final_instalment_amount: string
	total: string
}

export interface StudioQuotePaymentPlan extends StudioQuotePaymentPlanOption {
	options?: StudioQuotePaymentPlanOption[]
}

export interface StudioBulkJob {
	job_id: string
	status: 'pending' | 'running' | 'completed' | 'failed'
	total_rows: number
	succeeded?: number
	failed?: number
	results?: Array<{
		index: number
		status: 'ok' | 'failed' | 'error'
		quote_reference?: string | null
		premium?: string | null
		uw_outcome?: string | null
		errors?: Record<string, string[]> | string
	}>
	finished_at?: string | null
}

export interface StudioPaymentIntent {
	provider: string
	client_secret: string | null
	redirect_url: string | null
	publishable_key: string | null
	status: string
	intent_id: string | null
	billing?: Record<string, unknown>
}

export interface StudioPaymentMethod {
	/** Stable ProviderConfig id — the correct identifier to send back when
	 *  creating a payment intent, since provider names aren't unique (two
	 *  underwriters can each have their own Stripe account). */
	id: string
	channel_id: string
	provider: string
	label: string
	purpose: string
	mode: 'test' | 'live'
}

/**
 * One payment plan Studio will accept for a form — what to charge, in what
 * currency, and over how many instalments.
 *
 * Every field is optional on purpose. Studio omits the whole block on some
 * forms and returns `"0"` amounts whenever the reference has no quote behind it
 * yet (a payment-gated form pays *before* the submission exists), so a caller
 * that assumes these are present will silently charge a wrong or unitless
 * amount. Treat a missing `currency` as "do not charge".
 */
export interface StudioBillingOption {
	option?: string
	label?: string
	mode?: string
	recurring?: boolean
	frequency?: string
	instalments?: number
	/** Decimal strings, not numbers — parse before doing arithmetic. */
	total_premium?: string
	first_instalment_amount?: string
	/** ISO 4217. The only trustworthy source of the charge currency. */
	currency?: string
}

/** The selected plan, plus every plan the payer could switch to. */
export interface StudioBillingPlan extends StudioBillingOption {
	options?: StudioBillingOption[]
}

/** Full payload of `GET /forms/{code}/payment-methods/`. */
export interface StudioPaymentMethodsResponse {
	methods: StudioPaymentMethod[]
	billing: StudioBillingPlan | null
}

export interface StudioApiConfig {
	/** Base URL of the Studio backend. Trailing slash stripped automatically. */
	apiBase: string
	/** Tenant-scoped API key issued by Studio. */
	apiKey: string
}

/**
 * Build a Studio API client bound to a single tenant's config. Each tenant
 * app constructs its own client from its own STUDIO_API_URL/STUDIO_API_KEY
 * (or equivalent), instead of the client reading globals itself.
 */
export function createStudioApiClient(config: StudioApiConfig) {
	function base(): string {
		if (!config.apiBase) throw new Error('Studio API base URL is not set')
		return config.apiBase.replace(/\/$/, '')
	}

	function authHeaders(): Record<string, string> {
		if (!config.apiKey) throw new Error('Studio API key is not set')
		return {
			Accept: 'application/json',
			Authorization: `Bearer ${config.apiKey}`,
		}
	}

	async function fetchStudioFormSchema(
		formCode: string,
		locale = 'en',
		contract?: StudioFormContract
	): Promise<StudioFormSchemaResponse | StudioWizardFormSchemaResponse> {
		const params = new URLSearchParams({ locale })
		if (contract) params.set('contract', contract)
		const url = `${base()}/api/client/v1/forms/${formCode}/schema/?${params.toString()}`
		const res = await fetch(url, {
			headers: authHeaders(),
			signal: AbortSignal.timeout(12000),
		})
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new StudioApiError(res.status, body)
		}
		return res.json() as Promise<
			StudioFormSchemaResponse | StudioWizardFormSchemaResponse
		>
	}

	/**
	 * Fetch the RJSF-compatible form schema for a product line, resolved by
	 * product_code instead of a hardcoded form_code. Lets a tenant route
	 * (e.g. /forms/home) ask "whatever form is currently approved for this
	 * product" instead of baking a form_code into the tenant package —
	 * publishing a new approved FormDefinition for the same product_code
	 * updates what this returns with no tenant redeploy.
	 * Requires scopes: forms:read
	 */
	async function fetchStudioFormSchemaByProduct(
		productCode: string,
		locale = 'en'
	): Promise<StudioFormSchemaResponse> {
		const url = `${base()}/api/client/v1/products/${productCode}/schema/?locale=${locale}`
		const res = await fetch(url, {
			headers: authHeaders(),
			signal: AbortSignal.timeout(12000),
		})
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new StudioApiError(res.status, body)
		}
		return res.json() as Promise<StudioFormSchemaResponse>
	}

	/**
	 * Fetch the live VA for a section from the Studio Client API.
	 * Requires scope: va:read.
	 * Returns null when no VA is live for the section (404).
	 */
	async function fetchLiveVirtualAgent(
		section: string
	): Promise<LiveVirtualAgent | null> {
		const url = `${base()}/api/client/v1/va/live/?section=${encodeURIComponent(section)}`
		const res = await fetch(url, {
			headers: authHeaders(),
			signal: AbortSignal.timeout(12000),
		})
		if (res.status === 404) return null
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new StudioApiError(res.status, body)
		}
		return res.json() as Promise<LiveVirtualAgent>
	}

	/**
	 * Fetch Studio-configured header/footer nav (logo, nav links, social links,
	 * footer copy) from the Studio Client API.
	 * Requires scope: content:read
	 */
	async function fetchSiteNavigation(): Promise<StudioSiteNavigationResponse> {
		const url = `${base()}/api/client/v1/site-navigation/`
		const res = await fetch(url, {
			headers: authHeaders(),
			signal: AbortSignal.timeout(8000),
		})
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new StudioApiError(res.status, body)
		}
		return res.json() as Promise<StudioSiteNavigationResponse>
	}

	async function fetchTheme(): Promise<StudioThemeResponse> {
		const url = `${base()}/api/client/v1/theme/`
		const res = await fetch(url, {
			headers: authHeaders(),
			signal: AbortSignal.timeout(8000),
		})
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new StudioApiError(res.status, body)
		}
		return res.json() as Promise<StudioThemeResponse>
	}

	async function fetchPageSchema(
		slug: string,
		locale = ''
	): Promise<StudioPageSchema | null> {
		const params = new URLSearchParams()
		if (locale) params.set('locale', locale)
		const query = params.size ? `?${params}` : ''
		const res = await fetch(
			`${base()}/api/client/v1/pages/${encodeURIComponent(slug)}/schema/${query}`,
			{ headers: authHeaders(), signal: AbortSignal.timeout(12000) }
		)
		if (res.status === 404) return null
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new StudioApiError(res.status, body)
		}
		return res.json() as Promise<StudioPageSchema>
	}

	async function fetchPagePreviewSchema(
		slug: string,
		token: string,
		locale = ''
	): Promise<StudioPageSchema | null> {
		if (!token) throw new Error('Studio page preview token is not set')
		const params = new URLSearchParams({ token })
		if (locale) params.set('locale', locale)
		const res = await fetch(
			`${base()}/api/client/v1/pages/${encodeURIComponent(slug)}/preview-schema/?${params}`,
			{ headers: authHeaders(), signal: AbortSignal.timeout(12000) }
		)
		if (res.status === 404) return null
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new StudioApiError(res.status, body)
		}
		return res.json() as Promise<StudioPageSchema>
	}

	async function submitToStudio(
		formCode: string,
		data: Record<string, unknown>,
		sourceChannel = 'web',
		externalReference?: string
	): Promise<StudioSubmitResponse> {
		const url = `${base()}/api/client/v1/forms/${formCode}/submit/`
		const res = await fetch(url, {
			method: 'POST',
			headers: {
				...authHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				data,
				source_channel: sourceChannel,
				...(externalReference ? { external_reference: externalReference } : {}),
			}),
			signal: AbortSignal.timeout(10000),
		})
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new StudioApiError(res.status, body)
		}
		return res.json() as Promise<StudioSubmitResponse>
	}
	async function captureLeadInStudio(
		formCode: string,
		data: Record<string, unknown>,
		meta?: {
			reason?: string
			reason_code?: string
			triggered_rule?: string
			source?: string
		}
	): Promise<{ lead_id: string; status: string } | null> {
		try {
			const url = `${base()}/api/client/v1/forms/${formCode}/lead/`
			const res = await fetch(url, {
				method: 'POST',
				headers: { ...authHeaders(), 'Content-Type': 'application/json' },
				body: JSON.stringify({
					data,
					decline_reason: meta?.reason,
					reason_code: meta?.reason_code,
					triggered_rule: meta?.triggered_rule,
					source: meta?.source ?? 'declined_lead',
				}),
				signal: AbortSignal.timeout(8000),
			})
			if (!res.ok) return null
			return (await res.json()) as { lead_id: string; status: string }
		} catch {
			return null
		}
	}
	async function previewQuoteFromStudio(
		formCode: string,
		data: Record<string, unknown>
	): Promise<StudioQuotePreviewResponse> {
		const url = `${base()}/api/client/v1/forms/${formCode}/quote-preview/`
		const res = await fetch(url, {
			method: 'POST',
			headers: {
				...authHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({ data }),
			signal: AbortSignal.timeout(8000),
		})
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new Error(
				body?.detail ||
					body?.message ||
					`Studio quote-preview failed with status ${res.status}`
			)
		}
		return res.json() as Promise<StudioQuotePreviewResponse>
	}
	async function submitStudioBulk(
		formCode: string,
		rows: Array<Record<string, unknown>>,
		partnerReference?: string
	): Promise<StudioBulkJob> {
		const res = await fetch(
			`${base()}/api/client/v1/forms/${formCode}/bulk/`,
			{
				method: 'POST',
				headers: { ...authHeaders(), 'Content-Type': 'application/json' },
				body: JSON.stringify({ rows, partner_reference: partnerReference }),
			}
		)
		if (!res.ok) {
			const detail = await res.text()
			throw new Error(
				`Bulk submit failed (${res.status}): ${detail.slice(0, 300)}`
			)
		}
		return (await res.json()) as StudioBulkJob
	}

	/** Poll a bulk job's status + per-row results. */
	async function fetchStudioBulkJob(jobId: string): Promise<StudioBulkJob> {
		const res = await fetch(`${base()}/api/client/v1/bulk-jobs/${jobId}/`, {
			headers: authHeaders(),
		})
		if (!res.ok) throw new Error(`Bulk job fetch failed (${res.status})`)
		return (await res.json()) as StudioBulkJob
	}
	async function fetchStudioPaymentMethodsResponse(
		formCode: string,
		reference?: string,
		paymentOption?: string
	): Promise<StudioPaymentMethodsResponse> {
		const params = new URLSearchParams()
		if (reference) params.set('reference', reference)
		if (paymentOption) params.set('payment_option', paymentOption)
		const qs = params.toString()
		const res = await fetch(
			`${base()}/api/client/v1/forms/${formCode}/payment-methods/${qs ? `?${qs}` : ''}`,
			{
				headers: authHeaders(),
				signal: AbortSignal.timeout(10000),
			}
		)
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new StudioApiError(res.status, body)
		}
		const body = await res.json()
		return {
			methods: (body?.methods ?? []) as StudioPaymentMethod[],
			billing: (body?.billing ?? null) as StudioBillingPlan | null,
		}
	}
	async function fetchStudioPaymentMethods(
		formCode: string,
		reference?: string,
		paymentOption?: string
	): Promise<StudioPaymentMethod[]> {
		const { methods } = await fetchStudioPaymentMethodsResponse(
			formCode,
			reference,
			paymentOption
		)
		return methods
	}

	async function createStudioPaymentIntent(
		formCode: string,
		amountMinor: number,
		currency: string,
		reference: string,
		metadata?: Record<string, unknown>,
		provider?: string,
		paymentChannel?: string,
		paymentOption?: string
	): Promise<StudioPaymentIntent> {
		let selectedChannel = paymentChannel?.trim()
		let selectedProvider = provider?.trim()

		if (!selectedChannel) {
			const requestedProvider = provider?.trim()
			const paymentMethods = await fetchStudioPaymentMethods(
				formCode,
				reference,
				paymentOption
			)
			const match =
				paymentMethods.find(method => method.provider === requestedProvider) ||
				paymentMethods[0]

			if (!match) {
				throw new StudioApiError(503, {
					detail: `Studio returned no eligible payment method for form '${formCode}'.`,
					status: 'no_provider',
				})
			}
			selectedChannel = match.channel_id
			selectedProvider = match.provider
		}

		const res = await fetch(
			`${base()}/api/client/v1/forms/${formCode}/payment-intent/`,
			{
				method: 'POST',
				headers: { ...authHeaders(), 'Content-Type': 'application/json' },
				body: JSON.stringify({
					amount: amountMinor,
					currency,
					reference,
					...(paymentOption ? { payment_option: paymentOption } : {}),
					// The primary identifier — unambiguous even when two underwriters
					// share the same provider name.
					payment_channel: selectedChannel,
					// Still sent for older Studio deployments that only understand
					// provider name.
					...(selectedProvider ? { provider: selectedProvider } : {}),
					metadata,
				}),
				signal: AbortSignal.timeout(15000),
			}
		)
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new StudioApiError(res.status, body)
		}
		return (await res.json()) as StudioPaymentIntent
	}
	async function loginStudioAccount(
		formCode: string,
		email: string,
		password: string
	): Promise<{ ok: boolean; token: string; email: string }> {
		const res = await fetch(
			`${base()}/api/client/v1/forms/${formCode}/account/login/`,
			{
				method: 'POST',
				headers: { ...authHeaders(), 'Content-Type': 'application/json' },
				body: JSON.stringify({ email, password }),
			}
		)
		if (res.status === 401) {
			throw new StudioLoginError(401, 'Invalid email or password.')
		}
		if (!res.ok) {
			const detail = await res.text()
			throw new Error(
				`Studio login failed (${res.status}): ${detail.slice(0, 300)}`
			)
		}
		return (await res.json()) as { ok: boolean; token: string; email: string }
	}

	async function setStudioAccountPassword(
		formCode: string,
		reference: string,
		password: string,
		email?: string
	): Promise<{ ok: boolean; email?: string; policy_number?: string | null }> {
		const res = await fetch(
			`${base()}/api/client/v1/forms/${formCode}/account/set-password/`,
			{
				method: 'POST',
				headers: { ...authHeaders(), 'Content-Type': 'application/json' },
				body: JSON.stringify(email ? { reference, password, email } : { reference, password }),
			}
		)
		if (!res.ok) {
			const detail = await res.text()
			throw new Error(
				`Studio set-password failed (${res.status}): ${detail.slice(0, 300)}`
			)
		}
		return (await res.json()) as {
			ok: boolean
			email?: string
			policy_number?: string | null
		}
	}

	/**
	 * Request a password-reset email. Always resolves the same generic body
	 * whether or not the email has an account — Studio never confirms account
	 * existence to the caller, so the client shouldn't try to distinguish
	 * "sent" from "not found" either.
	 */
	async function requestStudioPasswordReset(
		formCode: string,
		email: string
	): Promise<{ detail: string }> {
		const res = await fetch(
			`${base()}/api/client/v1/forms/${formCode}/account/password-reset/request/`,
			{
				method: 'POST',
				headers: { ...authHeaders(), 'Content-Type': 'application/json' },
				body: JSON.stringify({ email }),
			}
		)
		if (!res.ok) {
			const detail = await res.text()
			throw new Error(
				`Studio password-reset request failed (${res.status}): ${detail.slice(0, 300)}`
			)
		}
		return (await res.json()) as { detail: string }
	}

	/**
	 * Confirm a password reset with the token from the emailed link. A 400
	 * (expired/already-used/invalid token, or password too short) is surfaced
	 * as a StudioApiError so the UI can show the server's own message.
	 */
	async function confirmStudioPasswordReset(
		formCode: string,
		token: string,
		password: string
	): Promise<{ ok: boolean; email: string }> {
		const res = await fetch(
			`${base()}/api/client/v1/forms/${formCode}/account/password-reset/confirm/`,
			{
				method: 'POST',
				headers: { ...authHeaders(), 'Content-Type': 'application/json' },
				body: JSON.stringify({ token, password }),
			}
		)
		if (!res.ok) {
			const body = await res.json().catch(() => ({}))
			throw new StudioApiError(res.status, body)
		}
		return (await res.json()) as { ok: boolean; email: string }
	}

	return {
		fetchStudioFormSchema,
		fetchStudioFormSchemaByProduct,
		fetchLiveVirtualAgent,
		fetchSiteNavigation,
		fetchTheme,
		fetchPageSchema,
		fetchPagePreviewSchema,
		submitToStudio,
		captureLeadInStudio,
		previewQuoteFromStudio,
		submitStudioBulk,
		fetchStudioBulkJob,
		fetchStudioPaymentMethods,
		fetchStudioPaymentMethodsResponse,
		createStudioPaymentIntent,
		loginStudioAccount,
		setStudioAccountPassword,
		requestStudioPasswordReset,
		confirmStudioPasswordReset,
	}
}

export type StudioApiClient = ReturnType<typeof createStudioApiClient>
